/**
 * The shell: session, navigation, and which view is on screen.
 *
 * Session state comes from `/api/auth/me` on load rather than from anything stored in
 * the browser. The cookie is HttpOnly, so the client genuinely cannot know whether it
 * is signed in — it has to ask, and asking is the only answer that cannot go stale.
 */

import { useEffect, useState } from "react";
import { EvidenceOpener, EvidencePanel } from "./components/EvidencePanel";
import { TopbarMenu } from "./components/TopbarMenu";
import { EvidenceProvider } from "./state/evidence";
import { ViewTabsProvider } from "./state/viewtabs";
import { api, ApiError, type Principal } from "./lib/api";
import { useRoute } from "./lib/route";
import { useSheetMode } from "./lib/sheet";
import { Audit } from "./views/Audit";
import { ArtifactTable } from "./views/ArtifactTable";
import { Datasets } from "./views/Datasets";
import { Dossier } from "./views/Dossier";
import { Files } from "./views/Files";
import { Inference } from "./views/Inference";
import { Login } from "./views/Login";
import { Providers } from "./views/Providers";
import { Search } from "./views/Search";
import { TableProfileReview } from "./views/TableProfileReview";
import { Workspace } from "./views/Workspace";
import "./theme.css";
import "./app.css";

type Session = { kind: "unknown" } | { kind: "out" } | { kind: "in"; principal: Principal };

export function App() {
  const [session, setSession] = useState<Session>({ kind: "unknown" });
  const route = useRoute();
  // Not a styling question, which is why it is not left to a media query: below this
  // width the same links are rendered somewhere else entirely, and rendering them in
  // both places and hiding one would leave every `data-testid` in the topbar matching
  // two elements, one of them permanently unclickable.
  const phone = useSheetMode();

  useEffect(() => {
    void api
      .me()
      .then((principal) => setSession({ kind: "in", principal }))
      .catch((cause: unknown) => {
        if (cause instanceof ApiError && cause.status === 401) setSession({ kind: "out" });
        else setSession({ kind: "out" });
      });
  }, []);

  if (session.kind === "unknown") {
    return <main className="page" data-testid="session-loading">…</main>;
  }
  if (session.kind === "out") {
    return <Login onLogin={(principal) => setSession({ kind: "in", principal })} />;
  }

  const { principal } = session;

  const signOut = () => void api.logout().then(() => setSession({ kind: "out" }));

  return (
    <EvidenceProvider>
      <ViewTabsProvider>
        <div className="shell">
          {/* On a phone every link below moves into `TopbarMenu` and this becomes a
              single 44px row: brand, the evidence opener when there is evidence to
              reopen, and the hamburger. Above that width nothing here changes. */}
          <header className="topbar">
            <a href="#/" className="brand">
              soulsearch
            </a>

            {!phone && "datasetId" in route && (
              <nav className="crumbs">
                <a href={`#/d/${route.datasetId}`} data-testid="nav-workspace">
                  Workspace
                </a>
                <a href={`#/d/${route.datasetId}/files`} data-testid="nav-files">
                  Files
                </a>
              </nav>
            )}

            {/* Renders itself away on a wide screen, where the panel is a column and
                there is nothing to reopen. Stays out of the menu: see TopbarMenu. */}
            <EvidenceOpener />

            <div className="spacer" />

            {!phone && principal.is_admin && (
              <nav className="crumbs">
                <a href="#/settings" data-testid="nav-settings">
                  Providers
                </a>
                <a href="#/audit" data-testid="nav-audit">
                  Audit
                </a>
              </nav>
            )}

            {!phone && (
              <span className="mono" data-testid="principal">
                {principal.email}
              </span>
            )}
            {!phone && (
              <button data-testid="logout" onClick={signOut}>
                Sign out
              </button>
            )}

            <TopbarMenu
              datasetId={"datasetId" in route ? route.datasetId : undefined}
              principal={principal}
              onSignOut={signOut}
            />
          </header>

          {route.name === "datasets" && <Datasets />}
          {route.name === "workspace" && <Workspace datasetId={route.datasetId} />}
          {route.name === "files" && <Files datasetId={route.datasetId} />}
          {route.name === "table-profile" && (
            <TableProfileReview datasetId={route.datasetId} fileId={route.fileId} />
          )}
          {route.name === "search" && (
            // Search gets the evidence panel too. The panel is furniture: a hit you
            // cannot immediately open in context is a search result, not evidence.
            <div className="workspace two-pane">
              <section className="pane left">
                <Search datasetId={route.datasetId} />
              </section>
              <EvidencePanel datasetId={route.datasetId} />
            </div>
          )}
          {route.name === "artifact" && (
            <ArtifactTable
              datasetId={route.datasetId}
              artifactId={route.artifactId}
              versionId={route.versionId}
            />
          )}
          {route.name === "dossier" && (
            <Dossier
              datasetId={route.datasetId}
              artifactId={route.artifactId}
              versionId={route.versionId}
            />
          )}
          {route.name === "inference" && (
            <Inference
              datasetId={route.datasetId}
              artifactId={route.artifactId}
              versionId={route.versionId}
            />
          )}
          {route.name === "settings" &&
            (principal.is_admin ? (
              <Providers />
            ) : (
              <main className="page" data-testid="not-admin">
                <p>Provider settings are administrator-only.</p>
              </main>
            ))}
          {route.name === "audit" &&
            (principal.is_admin ? (
              <Audit />
            ) : (
              <main className="page" data-testid="not-admin">
                <p>The audit log is administrator-only.</p>
              </main>
            ))}
        </div>
      </ViewTabsProvider>
    </EvidenceProvider>
  );
}
